import React from "react";
import {
  Loader2,
  Brain,
  GitBranch,
  Zap,
  MessageSquare,
  AlertCircle,
} from "lucide-react";
import {
  WorkflowRunStep,
  AgentWorkflowRunStepStatus,
} from "@/store/workflowRunStore";
import { WorkflowNodeType, getStepLoadingLabel } from "./helpers/stepHelpers";

interface WorkflowRunViewStepLoadingProps {
  step: WorkflowRunStep;
  nodeType: WorkflowNodeType;
  nodeName?: string;
}

/**
 * Displays a loading or error indicator for a workflow run step.
 * Shows when a step is RUNNING with no operations, or when a step has FAILED.
 */
export const WorkflowRunViewStepLoading: React.FC<
  WorkflowRunViewStepLoadingProps
> = ({ step, nodeType, nodeName }) => {
  const isLoading = step.status === AgentWorkflowRunStepStatus.RUNNING;
  const isFailed = step.status === AgentWorkflowRunStepStatus.FAILED;

  if (!isLoading && !isFailed) {
    return null;
  }

  if (isFailed && step.failureReason) {
    return (
      <div>
        <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 rounded px-2 py-1 w-full">
          <AlertCircle className="h-3 w-3 shrink-0" />
          <span>Step failed: {step.failureReason}</span>
        </div>
      </div>
    );
  }

  const icon = getIconForNodeType(nodeType);
  const label = getStepLoadingLabel(nodeType, nodeName);

  return (
    <div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted rounded px-2 py-1 w-full">
        {icon}
        <span className="flex-1">{label}</span>
        <Loader2 className="h-3 w-3 animate-spin" />
      </div>
    </div>
  );
};

/**
 * Returns the appropriate icon component for the given node type.
 */
function getIconForNodeType(nodeType: WorkflowNodeType): React.ReactNode {
  switch (nodeType) {
    case "AGENT":
      return <Brain className="h-3 w-3" />;
    case "CONDITION":
      return <GitBranch className="h-3 w-3" />;
    case "ACTION":
      return <Zap className="h-3 w-3" />;
    case "MESSAGE":
      return <MessageSquare className="h-3 w-3" />;
    default:
      return <Zap className="h-3 w-3" />;
  }
}
